/**
 * The QC Review's "QC flags" sheet, pure: per-check rows folded from `/qc-summary`, each finding's
 * decision status from the review export, and the sheet's severity / decision / search filters
 * applied to the findings so per-check counts, chips and lists all agree.
 */
import type { QcFindingRead, QcSummaryRead } from '../types/attribution.types';

import { DECISION_GLOSSARY } from './glossary';
import type { FlagVerdict } from './review-decisions';
import { qcFlagKey } from './review-decisions';
import { qcSeverityMeta } from './review-hints';
import type { StatusTone } from './run-status';

/** Severities in the sheet's chips, most severe first. */
export const QC_SEVERITIES = ['HIGH', 'MEDIUM', 'SUGGESTION', 'INFO'] as const;

/** One QC severity code. */
export type QcSeverity = (typeof QC_SEVERITIES)[number];

/** Counts per severity. */
export type SeverityCounts = Record<QcSeverity, number>;

/** One finding's decision status: no standing decision, or the reviewer's call. */
export type FlagStatus = 'open' | 'confirmed' | 'dismissed';

/** Decision status in words, tone and meaning (glossary labels) — shared by the List and Cards views. */
export const FLAG_STATUS_META: Record<
  FlagStatus,
  { label: string; tone: StatusTone; meaning: string }
> = {
  open: {
    label: 'Open',
    tone: 'muted',
    meaning: 'No standing decision yet. A decision that was taken back reads as open again.',
  },
  confirmed: {
    label: DECISION_GLOSSARY.FINDING_CONFIRMED.label,
    tone: 'error',
    meaning: DECISION_GLOSSARY.FINDING_CONFIRMED.meaning,
  },
  dismissed: {
    label: DECISION_GLOSSARY.FINDING_DISMISSED.label,
    tone: 'success',
    meaning: DECISION_GLOSSARY.FINDING_DISMISSED.meaning,
  },
};

/** The decision chip: open, any decision, or one of the two calls. */
export type DecisionFilter = 'open' | 'decided' | 'confirmed' | 'dismissed';

/** The sheet's filters; `undefined` means "All". `q` is already debounced and trimmed. */
export interface QcFlagFilters {
  severity: QcSeverity | undefined;
  decision: DecisionFilter | undefined;
  q: string;
}

/** One check from `/qc-summary`: flags per severity and affected products (exact counts). */
export interface QcCheckSummary {
  checkName: string;
  flags: number;
  bySeverity: SeverityCounts;
  products: number;
}

/** Zeroed severity counts. */
export function emptySeverityCounts(): SeverityCounts {
  return { HIGH: 0, MEDIUM: 0, SUGGESTION: 0, INFO: 0 };
}

/** A wire severity as one of the four codes (read leniently: unknown values count as Info). */
export function severityCode(severity: string): QcSeverity {
  const upper = severity.toUpperCase();
  return (QC_SEVERITIES as readonly string[]).includes(upper) ? (upper as QcSeverity) : 'INFO';
}

/** Folds `/qc-summary` (one row per check × severity) into one row per check, worst first. */
export function qcCheckSummaries(summary: readonly QcSummaryRead[]): QcCheckSummary[] {
  const byCheck = new Map<string, QcCheckSummary>();
  for (const row of summary) {
    const line = byCheck.get(row.checkName) ?? {
      checkName: row.checkName,
      flags: 0,
      bySeverity: emptySeverityCounts(),
      products: 0,
    };
    line.flags += row.findings;
    line.bySeverity[severityCode(row.severity)] += row.findings;
    // A product can sit under several severities of one check; the largest is the floor.
    line.products = Math.max(line.products, row.products);
    byCheck.set(row.checkName, line);
  }
  return [...byCheck.values()].sort(
    (a, b) =>
      b.bySeverity.HIGH - a.bySeverity.HIGH ||
      b.bySeverity.MEDIUM - a.bySeverity.MEDIUM ||
      b.flags - a.flags,
  );
}

/** A finding's decision status from the export's per-flag verdicts. */
export function flagStatus(
  finding: QcFindingRead,
  verdicts: ReadonlyMap<string, FlagVerdict> | undefined,
): FlagStatus {
  const verdict = verdicts?.get(qcFlagKey(finding));
  if (verdict === 'FINDING_CONFIRMED') return 'confirmed';
  if (verdict === 'FINDING_DISMISSED') return 'dismissed';
  return 'open';
}

/** Whether a status passes the decision chip. */
function decisionMatches(status: FlagStatus, decision: DecisionFilter | undefined): boolean {
  if (decision === undefined) return true;
  if (decision === 'decided') return status !== 'open';
  return status === decision;
}

/** Whether a finding matches the search: product id / feed id / title, attribute, values. */
function searchMatches(
  finding: QcFindingRead,
  q: string,
  productText: ReadonlyMap<string, string>,
): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return [
    finding.productId,
    productText.get(finding.productId) ?? '',
    finding.attributeName,
    finding.component,
    finding.attributeValue,
    finding.suggestedValue,
  ].some((text) => text.toLowerCase().includes(needle));
}

/** Which filters to apply — the chips' own counts leave their own filter out. */
interface FilterOptions {
  skipSeverity?: boolean;
  skipDecision?: boolean;
}

/** The findings passing the sheet's filters, most severe first then by product. */
export function filterFindings(
  findings: readonly QcFindingRead[],
  filters: QcFlagFilters,
  verdicts: ReadonlyMap<string, FlagVerdict> | undefined,
  productText: ReadonlyMap<string, string>,
  options: FilterOptions = {},
): QcFindingRead[] {
  return findings
    .filter(
      (finding) =>
        (options.skipSeverity === true ||
          filters.severity === undefined ||
          severityCode(finding.severity) === filters.severity) &&
        (options.skipDecision === true ||
          decisionMatches(flagStatus(finding, verdicts), filters.decision)) &&
        searchMatches(finding, filters.q, productText),
    )
    .sort(
      (a, b) =>
        qcSeverityMeta(severityCode(a.severity)).rank -
          qcSeverityMeta(severityCode(b.severity)).rank || a.productId.localeCompare(b.productId),
    );
}

/** Flags, severities, products and decisions over a set of findings. */
export interface FindingCounts {
  flags: number;
  bySeverity: SeverityCounts;
  products: number;
  confirmed: number;
  dismissed: number;
}

/** Counts over findings (already filtered). */
export function countFindings(
  findings: readonly QcFindingRead[],
  verdicts: ReadonlyMap<string, FlagVerdict> | undefined,
): FindingCounts {
  const bySeverity = emptySeverityCounts();
  const products = new Set<string>();
  let confirmed = 0;
  let dismissed = 0;
  for (const finding of findings) {
    bySeverity[severityCode(finding.severity)] += 1;
    products.add(finding.productId);
    const status = flagStatus(finding, verdicts);
    if (status === 'confirmed') confirmed += 1;
    else if (status === 'dismissed') dismissed += 1;
  }
  return { flags: findings.length, bySeverity, products: products.size, confirmed, dismissed };
}

/** The most-flagged attributes of a check: "Embellishments 5 · Style-Silhouette 3". */
export function topAttributes(
  findings: readonly QcFindingRead[],
  limit = 3,
): { name: string; flags: number }[] {
  const counts = new Map<string, number>();
  for (const finding of findings) {
    const name = finding.attributeName.trim();
    if (name !== '') counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, flags]) => ({ name, flags }))
    .sort((a, b) => b.flags - a.flags || a.name.localeCompare(b.name))
    .slice(0, limit);
}

/** One check of the sheet (List row or Cards group): summary, filtered findings and shown counts. */
export interface QcCheckView {
  summary: QcCheckSummary;
  findings: readonly QcFindingRead[];
  /** Null while the filtered counts wait on the findings read. */
  counts: FindingCounts | null;
  top: { name: string; flags: number }[];
  isLoading: boolean;
  isError: boolean;
  /** The check has more flags than its one capped read holds. */
  capped: boolean;
}
